import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import {
  createTransaction,
  getPersons,
  getProjectCategories,
  getProjectsList,
  updateTransaction,
} from '../api/api';
import { useActiveProject } from '../context/ActiveProjectContext';
import FlatpickrInput from './FlatpickrInput';
import StyledSelect from './StyledSelect';

const normalizeTimeValue = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.slice(0, 5);
};

export default function NewTransactionFormScreen({
  isOpen,
  onClose,
  onSaved,
  editData,
  initialProjectId = '',
  initialProjectName = '',
  lockProject = false,
}) {
  const { selectedProject, selectProject, refreshProjects } = useActiveProject();

  const [projects, setProjects] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [persons, setPersons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    project_id: '',
    project: '',
    type: 'Expense',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    transactionTime: '',
    currency: 'PKR',
    paymentMethod: 'Cash',
    description: '',
    category: '',
    category_name: '',
    person: '',
    referenceNo: '',
  });

  const typeOptions = useMemo(() => [ 'Income',
  'Expense',
  'Transfer',
  'Refund',
  'Adjustment'], []);
  const currencyOptions = useMemo(() => ['PKR', 'USD', 'EUR', 'SAR'], []);
  const paymentMethods = useMemo(
    () => ['Cash', 'Bank Transfer', 'Card', 'Cheque', 'Online'],
    []
  );

  const typeDropdownOptions = useMemo(
    () => typeOptions.map((item) => ({ value: item, label: item })),
    [typeOptions]
  );

  const currencyDropdownOptions = useMemo(
    () => currencyOptions.map((item) => ({ value: item, label: item })),
    [currencyOptions]
  );

  const paymentDropdownOptions = useMemo(
    () => paymentMethods.map((item) => ({ value: item, label: item })),
    [paymentMethods]
  );

  const staticCategoryOptions = useMemo(
    () => ['— None —', 'Office Supplies', 'Travel', 'Food', 'Utilities', 'Marketing'],
    []
  );

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const res = await getProjectsList();
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setProjects(list);
      return list;
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadCategories = async (projectId) => {
    const pid = String(projectId || '').trim();
    if (!pid) {
      setApiCategories([]);
      return [];
    }

    try {
      const res = await getProjectCategories(pid);
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setApiCategories(list);
      return list;
    } catch {
      setApiCategories([]);
      return [];
    }
  };

  const loadPersons = async () => {
    try {
      const res = await getPersons();
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setPersons(list);
    } catch {
      setPersons([]);
    }
  };

  const projectDropdownOptions = useMemo(
    () =>
      projects.map((item) => ({
        value: String(item.id),
        label: item.name,
      })),
    [projects]
  );

  const categoryDropdownOptions = useMemo(() => {
    const apiOptions = apiCategories.map((c) => ({
      value: `api:${c.id}`,
      label: c.name,
    }));

    const fallbackOptions = staticCategoryOptions.map((label) =>
      label === '— None —'
        ? { value: '', label }
        : { value: `static:${label}`, label }
    );

    return apiOptions.length > 0
      ? [{ value: '', label: '— None —' }, ...apiOptions]
      : fallbackOptions;
  }, [apiCategories, staticCategoryOptions]);

  const personDropdownOptions = useMemo(() => {
    return [
      { value: '', label: 'Select person' },
      ...persons.map((item) => ({
        value: String(item.name || item.PersonName || item.full_name || ''),
        label: String(item.name || item.PersonName || item.full_name || ''),
      })),
    ];
  }, [persons]);

  useEffect(() => {
    if (!isOpen) return;

    const init = async () => {
      setError('');
      setSaving(false);

      const list = await loadProjects();
      await loadPersons();

      const fallbackProject = editData?.project_id
        ? list.find((p) => String(p.id) === String(editData.project_id)) || {
            id: editData.project_id,
            name: editData.project_name || '',
          }
        : initialProjectId
          ? list.find((p) => String(p.id) === String(initialProjectId)) || {
              id: initialProjectId,
              name: initialProjectName || '',
            }
          : selectedProject;

      const fallbackProjectId = String(fallbackProject?.id || '').trim();
      await loadCategories(fallbackProjectId);

      if (editData) {
        setForm({
          project_id: String(editData.project_id || fallbackProjectId || ''),
          project: editData.project_name || fallbackProject?.name || '',
          type: editData.type === 'receipt' ? 'Income' : 'Expense',
          amount: editData.amount || '',
          date: editData.date || new Date().toISOString().split('T')[0],
          transactionTime: normalizeTimeValue(editData.transaction_time || editData.time || ''),
          currency: editData.currency || 'PKR',
          paymentMethod: editData.payment_method || editData.paymentMethod || 'Cash',
          description: editData.description || '',
          category: String(editData.category_id || ''),
          category_name: editData.category_name || '',
          person: editData.person || '',
          referenceNo: editData.reference_no || editData.referenceNo || '',
        });
        return;
      }

      setForm({
        project_id: fallbackProjectId,
        project: fallbackProject?.name || '',
        type: 'Expense',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        transactionTime: '',
        currency: 'PKR',
        paymentMethod: 'Cash',
        description: '',
        category: '',
        category_name: '',
        person: '',
        referenceNo: '',
      });
    };

    init();
  }, [isOpen, editData, initialProjectId, initialProjectName, selectedProject]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleProjectChange = async (projectId) => {
    const project = projects.find((item) => String(item.id) === String(projectId));
    if (!project) return;

    setForm((prev) => ({
      ...prev,
      project_id: String(project.id),
      project: project.name || '',
      category: '',
      category_name: '',
    }));

    if (!lockProject) {
      await selectProject(project);
    }

    await loadCategories(project.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const projectId = String(form.project_id || '').trim();
    const amount = parseFloat(String(form.amount || '').trim());

    if (!projectId) {
      setError('Project is required');
      return;
    }
    if (!form.description.trim()) {
      setError('Description is required');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }
    if (!form.date) {
      setError('Date is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        project_id: Number(projectId),
        description: form.description.trim(),
        type: form.type === 'Income' ? 'receipt' : 'payment',
        amount,
        date: form.date,
        transaction_time: form.transactionTime || null,
        currency: form.currency,
        payment_method: form.paymentMethod,
        person: form.person.trim(),
        reference_no: form.referenceNo.trim(),
      };

      if (form.category) {
        payload.category_id = Number(form.category);
      } else if (form.category_name.trim()) {
        payload.category_name = form.category_name.trim();
      }

      if (editData?.id || editData?.transaction_id) {
        await updateTransaction(editData.id || editData.transaction_id, payload);
      } else {
        await createTransaction(payload);
      }

      await refreshProjects?.();
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-slate-50/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-slate-200">
          <h1 className="text-2xl font-bold text-slate-900">
            {editData ? 'Edit Transaction' : 'New Transaction'}
          </h1>
          <button
            type="button"
            className="w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
            aria-label="Close"
            onClick={onClose}
            disabled={saving}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="px-6 sm:px-8 py-6 sm:py-8 overflow-y-auto max-h-[calc(92vh-84px)]"
        >
          {error ? (
            <div
              className="mb-4 rounded-xl px-4 py-3 text-sm border-l-4"
              style={{
                background: '#fce0e8',
                color: '#c44569',
                borderLeftColor: '#c44569',
              }}
            >
              {error}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-5">
            <div>
              <label className="block text-slate-600 font-medium mb-2">Project</label>
              {lockProject ? (
                <div className="w-full h-12 rounded-xl border border-slate-300 px-4 text-slate-900 bg-slate-100 flex items-center font-semibold">
                  {form.project || initialProjectName || selectedProject?.name || 'Selected Project'}
                </div>
              ) : (
                <StyledSelect
                  value={form.project_id}
                  onChange={handleProjectChange}
                  options={projectDropdownOptions}
                  placeholder="— Select Project —"
                  disabled={saving || loadingProjects}
                  searchable
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-slate-600 font-medium mb-2">
                  Type <span className="text-rose-500">*</span>
                </label>
                <StyledSelect
                  value={form.type}
                  onChange={(next) => setForm((prev) => ({ ...prev, type: next }))}
                  options={typeDropdownOptions}
                  placeholder="Select type"
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block text-slate-600 font-medium mb-2">
                  Amount <span className="text-rose-500">*</span>
                </label>
                <input
                  type="number"
                  name="amount"
                  value={form.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="ems-input"
                  disabled={saving}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="block text-slate-600 font-medium mb-2">
                  Date <span className="text-rose-500">*</span>
                </label>
                <FlatpickrInput
                  value={form.date}
                  onChange={(d) => setForm((prev) => ({ ...prev, date: d }))}
                  disabled={saving}
                  className="!h-12 !rounded-xl !border-slate-300 !bg-white !px-4 !text-slate-900"
                  options={{ dateFormat: 'Y-m-d', animate: true, disableMobile: false }}
                />
              </div>

              <div>
                <label className="block text-slate-600 font-medium mb-2">Time</label>
                <FlatpickrInput
                  value={form.transactionTime}
                  onChange={(t) => setForm((prev) => ({ ...prev, transactionTime: t }))}
                  disabled={saving}
                  className="!h-12 !rounded-xl !border-slate-300 !bg-white !px-4 !text-slate-900"
                  placeholder="Select time"
                  options={{
                    noCalendar: true,
                    enableTime: true,
                    dateFormat: 'H:i',
                    time_24hr: true,
                    animate: true,
                    disableMobile: false,
                  }}
                />
              </div>

              <div>
                <label className="block text-slate-600 font-medium mb-2">Currency</label>
                <StyledSelect
                  value={form.currency}
                  onChange={(next) => setForm((prev) => ({ ...prev, currency: next }))}
                  options={currencyDropdownOptions}
                  placeholder="Select currency"
                  disabled={saving}
                  searchable
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-600 font-medium mb-2">Payment Method</label>
              <StyledSelect
                value={form.paymentMethod}
                onChange={(next) => setForm((prev) => ({ ...prev, paymentMethod: next }))}
                options={paymentDropdownOptions}
                placeholder="Select payment method"
                disabled={saving}
              />
            </div>

            <div>
              <label className="block text-slate-600 font-medium mb-2">Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={4}
                placeholder="What was this for?"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-4 focus:ring-rose-100 focus:border-rose-400 transition resize-none"
                disabled={saving}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-slate-600 font-medium mb-2">Category</label>
                <StyledSelect
                  value={
                    form.category
                      ? `api:${form.category}`
                      : form.category_name
                        ? `static:${form.category_name}`
                        : ''
                  }
                  onChange={(val) => {
                    if (!val) {
                      setForm((prev) => ({ ...prev, category: '', category_name: '' }));
                    } else if (String(val).startsWith('api:')) {
                      const id = String(val).slice(4);
                      const cat = apiCategories.find((c) => String(c.id) === id);
                      setForm((prev) => ({
                        ...prev,
                        category: id,
                        category_name: cat?.name || '',
                      }));
                    } else if (String(val).startsWith('static:')) {
                      const name = String(val).slice(7);
                      setForm((prev) => ({
                        ...prev,
                        category: '',
                        category_name: name,
                      }));
                    }
                  }}
                  options={categoryDropdownOptions}
                  placeholder="— None —"
                  disabled={saving}
                  searchable
                />
              </div>

              <div>
                <label className="block text-slate-600 font-medium mb-2">Person</label>
                <StyledSelect
                  value={form.person}
                  onChange={(next) => setForm((prev) => ({ ...prev, person: next }))}
                  options={personDropdownOptions}
                  placeholder="Select person"
                  disabled={saving}
                  searchable
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-600 font-medium mb-2">Reference No.</label>
              <input
                type="text"
                name="referenceNo"
                value={form.referenceNo}
                onChange={handleChange}
                placeholder="Invoice / receipt number"
                className="ems-input"
                disabled={saving}
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="ems-btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving}
                className="ems-btn-primary disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Transaction'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}